import { ImageGridFormFieldValue, NameValuePair } from "../../Components/FormFields/FormField"
import { VersionCompare } from "../../Constants/utils"

export enum MisTag
{
    WORLD_UPDATE = "WORLD_UPDATE",
    DYN_BUNDLE = "DYN_BUNDLE",
    FORCE_PACK = "FORCE_PACK",
    FLIGHT_SIM = "FLIGHT_SIM",
    LEGACY = "LEGACY",
    STORE_OFFICIAL = "STORE_OFFICIAL",
    DEPRECATED = "DEPRECATED",
    DISCOUNT_DELUXE_OR_PREMIUM = "DISCOUNT_DELUXE_OR_PREMIUM",
    STORE_INSTALL_PC = "STORE_INSTALL_PC",
    PACK_ITEM = "PACK_ITEM",
    NOT_AN_UPGRADE = "NOT_AN_UPGRADE",
    SIGN_OFF_PC = "SIGN_OFF_PC",
    SIGN_OFF_XBOX = "SIGN_OFF_XBOX",
    HIDE_FROM_INVENTORY = "HIDE_FROM_INVENTORY"
}

export enum MisPackageContentType
{
    Unknown = "UNKNOWN",
    Aircraft = "AIRCRAFT",
    Bundle = "BUNDLE",
    Currency = "CURRENCY",
    CurrencyPack = "CURRENCY_PACK",
    Instruments = "INSTRUMENTS",
    Livery = "LIVERY",
    Misc = "MISC",
    Mission = "MISSION",
    Pack = "PACK",
    Recipe = "RECIPE",
    Scenery = "SCENERY",
    Subscription = "SUBSCRIPTION",
    Widget = "WIDGET"
}

enum MisPublishingPackageType
{
    Unknown = "Unknown",
    Bundle = "Bundle",
    InternalGroup = "InternalGroup",
    Pack = "Pack",
    Package = "Package",
    Subscription = "Subscription",
}

enum MisPublishingItemType
{
    Unknown = "Unknown",
    Package = "Package",
    PublishingGroup = "PublishingGroup",
}

export enum MisSignOffState
{
    PendingVerification = "PendingVerification",
    Unsupported = "Unsupported",
    Incompatible = "Incompatible",
    Compatible_MarketplacePending = "Compatible_MarketplacePending",
    Compatible_NeverFeaturedInMarketplace = "Compatible_NeverFeaturedInMarketplace",
    Compatible_FeaturedInMarketplace = "Compatible_FeaturedInMarketplace"
}

export enum SubmissionType
{
    None = "None",
    Draft = "Draft",
    Prerelease = "Prerelease",
    Release = "Release",
    Error = "Error"
}

export enum RepositoryType
{
    Draft = "Draft",
    Live = "Live"
}

export enum MisMediaAssetTag
{
    Background = "Background",
    Carrousel = "Carrousel",
    Thumbnail = "Thumbnail",
    Library = "Library",
    Livery = "Livery"
}

export const MisMediaAssetTagLabels: Record<MisMediaAssetTag, string> = {
    [MisMediaAssetTag.Background]: "Product Page",
    [MisMediaAssetTag.Carrousel]: "Media Viewer",
    [MisMediaAssetTag.Thumbnail]: "Marketplace",
    [MisMediaAssetTag.Library]: "My Library",
    [MisMediaAssetTag.Livery]: "Livery Carrousel"
};

export enum MisPricesInUSD {
    Default = "Default",
    Aneida = "Aneida",
}

export interface DoubleRentalPrice {
    price: string,
    durationInDays: number
}

export interface MarketplacePriceDetails {
    catalogPrice?: string,
    upgradePrice?: string,
    rentalPrices?: DoubleRentalPrice[]
    bFreeTrial: boolean
}

export interface MisBusinessInfo
{
    thirdPartyShortName: string,
    thirdPartyUaid?: string,
    releaseDate?: string

    pricesInUSD: Record<MisPricesInUSD, MarketplacePriceDetails>
    freeTrial?: MisFreeTrial
}

interface MisMarketplaceInfo
{
    sections: MisSection[],
    locations?: MisLocation[],
    aircraftSpecifications?: NameValuePair[],
    tags?: MisTag[],
    keywords?: string[],
    creator?: string,
    supportContact?: string,
    secondSupportContactLabel?: string,
    secondSupportContactLink?: string,
    title: string,
    layout?: MisMarketplaceLayout,
    packageContentType: MisPackageContentType,
    publishingPackageType?: MisPublishingPackageType,
    publishingItemType?: MisPublishingItemType,
    referencedPackages?: string[]
}

interface MisSection
{
    category: string,
    section: string
}

export interface MisLocation
{
    type: string,
    airportCode: string,
    latLong: MisLatLong
}

interface MisLatLong
{
    latitude: string,
    longitude: string
}

export interface MisMarketplaceLayout
{
    mediaAssets: MisMediaAsset[],
    videoAssets?: MisVideoAsset[]
}

export interface MisMediaAsset {
    filename: string,
    tags: string[]
}

export interface MisVideoAsset {
    filename: string,
    thumbnailFilename: string[]
}

export interface MisPackageContentFileProperties
{
    name: string,
    url: string,
    contentHash: string
}

export interface MisFreeTrial
{
    version: number
}

export enum MisStatus
{
    Unset = "Unset",
    Live = "Live",
    WaitingForFileUpload = "WaitingForFileUpload",
    Commited = "Commited",
    Processing = "Processing",
    Error = "Error"
}

export interface MarketplaceMetadata {
    isArchived: boolean;
}

export interface MisPublicationFilter {
    signedOffPlatforms?: PublishingTarget[],
    signedOffStates?: Partial<Record<PublishingTarget, MisSignOffState>>,
    blackListedPlatforms?: PublishingTarget[]
}

export interface MisReleaseNote {
    lastUpdate?: string,
    olderHistory?: string
}

export interface MisReleaseInformation {
    packageName?: string,
    platforms: string[],
    releaseNotes?: Record<string, MisReleaseNote>,
    activatedByFlows?: string,
    perVariationIdContainerTitle?: Record<string, string>
}

export interface MisPackageDetailsResponse {
    packageName: string,
    playfabTitleId: string,
    repository?: string,
    businessInfo: MisBusinessInfo,
    submissionId?: string,
    publisherId?: string,
    developerId?: string,
    releaseStatus?: SubmissionType,
    ingestionStatus?: MisStatus,
    ingestionVersion?: number,
    latestFreeTrialVersion?: number,
    metadata?: MarketplaceMetadata,
    marketplaceInfo: MisMarketplaceInfo,
    files: MisPackageContentFileProperties[],
    localizedTexts: any,
    releaseInformation?: MisReleaseInformation,
    publicationFilter?: MisPublicationFilter,
    isAPackItem?: boolean,
    liveDate?: string,
    commitedDate?: string
}

export interface MarketplaceIngestRequest
{
    submissionId: string;
    businessInfo: MisBusinessInfo;
    packageName: string,
    developerId: string,
    publisherId: string,
    metadata?: MarketplaceMetadata,
    marketplaceInfo: MisMarketplaceInfo,
    filenameUploadUrls?: any,
    deletedFilenames?: string[],
    deleteExistingFiles: boolean,
    localizedTexts?: any,
    waitForContentIngestion: boolean,
}

export interface MarketplacePackItemIngestRequest {
    submissionId: string;
    packageName: string,
    developerId: string,
    publisherId: string,
    airportLocations?: MisLocation[],
    title: string,
    myLibraryThumbnailUrl?: string,
    packageContentType: MisPackageContentType
}

export interface MarketplaceSummary {
    ingestionVersion: number,
    submissionId: string,
    packageName: string,
    publisherId: string,
    developerId: string,
    priceInUSD: string,
    upgradePriceInUSD?: string,
    aneidaPriceInUSD?: string,
    aneidaUpgradePriceInUSD?: string,
    rentalInfo?: DoubleRentalPrice[],
    aneidaRentalInfo?: DoubleRentalPrice[],
    releaseStatus: SubmissionType,
    ingestionStatus: MisStatus,
    title: string,
    creator: string,
    packageContentType: MisPackageContentType,
    publishingItemType: MisPublishingItemType,
    category: string,
    section: string,
    description: string,
    keywords: string[],
    isAPackItem?: boolean,
    isArchived?: boolean,
    liveDate?: string,
    commitedDate?: string
    referencedPackages?: string[]
}

export type PlatformDetails = Record<PublishingTarget, CidsPackageDetailsResponse>;

export interface PackageDetails<T> {
    packageName: string,
    publisherId: string,
    developerId: string,
    lastModified: string,
    draft: T,
    prerelease: T,
    release: T,
}

export interface ContentPackageDetails extends PackageDetails<PlatformDetails> {
    pcProcessingStatus: SubmissionType,
    xboxProcessingStatus: SubmissionType,
    aneidaProcessingStatus: SubmissionType,
}

export enum AdoStatus {
    ProductQueued = "Product Queued",
    ProductTransferred = "Product Transferred",
    PartnerActionRequired = "Partner Action Required",
    ProductResubmitted = "Product Resubmitted",
    TestingRequested = "Testing Requested",
    ReadyForTesting = "Ready for Testing",
    TestingInProgress = "Testing In Progress",
    TestingCompleted = "Testing Completed",
    ReadyForRelease = "Ready For Release",
    SentForRelease = "Sent For Release",
    ProductReleased = "Product Released",
    ReleaseArchived = "Release Archived",
    ReleaseError = "Release Error"
}

export interface AdoStatusDetails {
    workItemId?: number;
    submissionId?: string;
    status?: AdoStatus;
    changedDate?: string;
}

export interface AdoStatusSummary {
    packageName?: string;
    marketplaceAdoStatus: AdoStatusDetails;
    contentAdoStatus: Record<PublishingTarget, AdoStatusDetails>;
}

// Response of GET api/publish/{packageName}/ado-work-items (internal users only). The links are
// composed server side so the ADO organization and project stay in API configuration.
export interface AdoWorkItemLink {
    // "Marketplace" for the marketplace work item, otherwise the publishing target (PC, Xbox, Aneida).
    target?: string;
    workItemId: number;
    status?: string;
    // Empty when the API could not resolve the organization or project; render the id as plain text.
    webUrl?: string;
}

export interface AdoWorkItemLinksResponse {
    packageName?: string;
    workItems: AdoWorkItemLink[];
}

// Response of GET api/publish/{packageName}/spm-links (internal users only). The links are composed
// server side from SpmSettings so the environment specific SPM host stays in API configuration and
// is never baked into the client bundle.
export interface SpmPackageLink {
    // "Draft" for the developer content repository, "Live" for the shared released one.
    source?: string;
    contentRepository?: string;
    // Empty when the SPM host is not configured; render the repository as plain text.
    webUrl?: string;
}

export interface SpmPackageLinksResponse {
    packageName?: string;
    links: SpmPackageLink[];
}

export interface MarketplaceSummaryData extends PackageDetails<MarketplaceSummary> {
    processingStatus?: SubmissionType
    adoStatus?: AdoStatusSummary
}

export interface MarketplacePricePair {
    priceInUSD?: number | string | null;
    aneidaPriceInUSD?: number | string | null;
}

export interface MarketplacePricesEntry {
    release?: MarketplacePricePair | null;
    prerelease?: MarketplacePricePair | null;
}

export interface ContentMetadata {
    wasmRequired: string;
    wasmFunctionality: string;
}

export interface CidsPackageDetailsResponse {
    packageName: string;
    contentRepository: string;
    version?: string;
    minimumGameVersion?: string;
    submissionId?: string;
    developerId?: string;
    publisherId?: string;
    metadata?: ContentMetadata;
    publishingTarget?: PublishingTarget;
    releaseStatus?: SubmissionType;
    isIngested?: boolean;
    isIngestionError?: boolean;
    isReleased?: boolean;
    creationDateUtc?: Date;
    ingestionDateUtc?: Date;
}

export enum PublishingTarget
{
    Xbox = "Xbox",
    PC = "PC",
    PCAndXbox = "PCAndXbox",
    Aneida = "Aneida",
    PCAndAneida = "PCAndAneida",
    XboxAndAneida = "XboxAndAneida",
    All = "All"
}

export interface ProductReleaseRequest
{
    packageName?: string;
    developerId?: string;
    publisherId?: string;
    minimumGameVersion?: string;
    marketplaceRelease?: boolean;
    contentRelease?: boolean;
    publishingTargets?: PublishingTarget[];
    creatorType?: CreatorType;
}

export interface ProductReleaseResponse
{
    packageName?: string;
    MarketplaceVersion?: ProductReleaseDetails;
    contentVersion?: Record<PublishingTarget, ProductReleaseDetails>;
}

export interface ProductReleaseDetails {
    packageDetails?: MisPackageDetailsResponse | CidsPackageDetailsResponse;
    isSuccess? : boolean;
}

export enum CreatorType {
    FirstParty = "FirstParty",
    ThirdParty = "ThirdParty"
}

export interface ContentIngestionRequest {
    submissionId?: string;
    packageName?: string;
    publishingTarget?: PublishingTarget;
    publisherId?: string;
    developerId?: string;
    minimumGameVersion?: string;
    metadata?: ContentMetadata;
    creatorType?: CreatorType;
}

export interface CidsReingestRequest {
    submissionId: string;
    packageName: string;
    publisherId: string;
    developerId: string;
    minimumGameVersionOverride?: string;
    fromPackageVersion: string;
    fromPublishingTarget: PublishingTarget;
    toPublishingTarget: PublishingTarget;
}

export interface ErrorData {
    packageName: string;
    submissionId: string;
    lastModified: string;
    errorMessage: string;
    publicSupplementalInformation?: Record<string, string>;
    privateSupplementalInformation?: Record<string, string>;
}

export enum CarryOverStatus {
    TriggerInitialCarryOver = "Trigger Initial Carry Over",
    TriggerCarryOverTransfer = "Trigger Carry Over Transfer",
    SentQueueMessage = "Sent Queue Message",
    StartIngestionSuccessful = "Start Ingestion Successful",
    CommitIngestionSuccessful = "Commit Ingestion Successful",
    StartReleaseSuccessful = "Start Release Successful",
    InvalidFieldsForIngestion = "Invalid Fields for Ingestion",
    IngestionFailed = "Ingestion Failed",
    StartIngestionFailed = "Start Ingestion Failed",
    CommitIngestionFailed = "Commit Ingestion Failed",
    ReleaseFailed = "Release Failed",
    StartReleaseFailed = "Start Release Failed",
    IngestionSuccessful = "Ingestion Successful",
    ReleaseSuccessful = "Release Successful"
}
    
export enum CarryOverAggregateStatus {
    NotStarted = "Not Started",
    InProgress = "In Progress",
    PortFailed = "Port Failed",
    InPrerelease = "In Prerelease",
    InRelease = "In Release",
    ReleasePending = "Release Pending"
}

export interface CategorySections {
    [key: string]: string[];
}

export const categorySections: CategorySections = {
    "AIRCRAFT": [
        "AIRPLANE",
        "AVIONIC",
        "GLIDER",
        "HELICOPTER",
        "LIGHTERTHANAIR",
        "OTHER"
    ],
    "AIRPORTS": [
        "AIRPORT",
        "HELIPORT",
        "SEAPLANEBASE",
        "OTHER"
    ],
    "WORLD": [
        "LANDMARK",
        "SCENERY",
        "ENVIRONMENT",
        "WORLDUPDATE",
        "TRAFFIC",
        "WILDLIFE",
        "GROUNDSERVICE",
        "LIGHTING",
        "OTHER"
    ],
    "ACTIVITIES": [
        "TRAINING",
        "CHALLENGE",
        "EXCURSION",
        "COMMERCIAL",
        "RACE",
        "OTHER"
    ],
    "CUSTOMIZATION": [
        "LIVERY",
        "SOUNDPACKAGE",
        "CHARACTER",
        "EFBAPPLICATION",
        "TOOL",
        "INPUTPROFILE",
        "OTHER"
    ]
}

export function getLatestMarketplacePackageSummaryResponse(packageDetails?: MarketplaceSummaryData): MarketplaceSummary | undefined {
    let prereleaseOrRelease = new Date(packageDetails?.prerelease?.commitedDate ?? 0) > new Date(packageDetails?.release?.commitedDate ?? 0) ? packageDetails?.prerelease : packageDetails?.release;
    let latest = new Date(packageDetails?.draft?.commitedDate ?? 0) > new Date(prereleaseOrRelease?.commitedDate ?? 0) ? packageDetails?.draft : prereleaseOrRelease;

    return latest;
}

export function getLatestMarketplacePackageDetailsResponse(packageDetails: PackageDetails<MisPackageDetailsResponse>): MisPackageDetailsResponse {
    let prereleaseOrRelease = new Date(packageDetails.prerelease?.commitedDate ?? 0) > new Date(packageDetails.release?.commitedDate ?? 0) ? packageDetails.prerelease : packageDetails.release;
    let latest = new Date(packageDetails.draft?.commitedDate ?? 0) > new Date(prereleaseOrRelease?.commitedDate ?? 0) ? packageDetails.draft : prereleaseOrRelease;

    return latest;
}

export function getLatestContentPackageDetailsResponse(packageDetails?: ContentPackageDetails): PlatformDetails {
    let prereleaseOrReleasePC = VersionCompare(packageDetails?.prerelease?.PC?.version, packageDetails?.release?.PC?.version) < 0 ? packageDetails?.prerelease?.PC : packageDetails?.release?.PC;
    let prereleaseOrReleaseXbox = VersionCompare(packageDetails?.prerelease?.Xbox?.version, packageDetails?.release?.Xbox?.version) < 0 ? packageDetails?.prerelease?.Xbox : packageDetails?.release?.Xbox;
    let prereleaseOrReleaseAneida = VersionCompare(packageDetails?.prerelease?.Aneida?.version, packageDetails?.release?.Aneida?.version) < 0 ? packageDetails?.prerelease?.Aneida : packageDetails?.release?.Aneida;

    let latest = {
        PC: VersionCompare(packageDetails?.draft?.PC?.version, prereleaseOrReleasePC?.version) < 0 ? packageDetails?.draft?.PC : prereleaseOrReleasePC,
        Xbox: VersionCompare(packageDetails?.draft?.Xbox?.version, prereleaseOrReleaseXbox?.version) < 0 ? packageDetails?.draft?.Xbox : prereleaseOrReleaseXbox,
        Aneida: VersionCompare(packageDetails?.draft?.Aneida?.version, prereleaseOrReleaseAneida?.version) < 0 ? packageDetails?.draft?.Aneida : prereleaseOrReleaseAneida
    } as PlatformDetails;

    return latest;
}

export function getPackageNameNoPrefix(packageName?: string): string | undefined {
    return packageName?.replace(/^fs20-/, "")?.replace(/^fs24-/, "");
}

export function getPackageName2020Prefix(packageName?: string): string | undefined {
    return  "fs20-" + getPackageNameNoPrefix(packageName);
}

export function getPackageName2024Prefix(packageName?: string): string {
    return  "fs24-" + getPackageNameNoPrefix(packageName);
}

export function isReleaseInProgress(status?: string): boolean {
    return status != null && status !== AdoStatus.ReleaseArchived && status !== AdoStatus.ProductReleased && status !== AdoStatus.PartnerActionRequired;
}

export function isResubmissionRequested(status?: string): boolean {
    return status != null && status === AdoStatus.PartnerActionRequired;
}

// A release that transferred to Prerelease but whose ADO work item message never made it onto the
// service bus leaves the optimistic "Product Queued" status behind with no work item id, because the
// work item id is only populated once ADO work item processing actually created/updated the item.
//
// That same shape is also the normal state for any release still in flight: QueueToAdoWorkItemProcessing
// writes it unconditionally, before it is known whether the enqueue succeeded, and nothing distinguishes
// a failed send from a message still waiting to be processed. Age is therefore the only available signal
// — a package still sitting in this state long after the queue and its retries should have drained is one
// where the message was lost.
export const MISSING_WORK_ITEM_MIN_AGE_MS = 10 * 60 * 1000;

// The API writes these timestamps with DateTime.UtcNow.ToString(), which emits no timezone designator
// even though the value is UTC. Parsing such a string with the Date constructor would treat it as
// browser-local time and make it appear future-dated west of UTC, so the UTC intent is applied here.
export function parseAdoChangedDateUtc(value?: string): number | undefined {
    const trimmed = value?.trim();
    if (!trimmed) {
        return undefined;
    }

    if (/(Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)) {
        const parsed = Date.parse(trimmed);
        return isNaN(parsed) ? undefined : parsed;
    }

    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(trimmed)) {
        const parsed = Date.parse(`${trimmed.replace(' ', 'T')}Z`);
        return isNaN(parsed) ? undefined : parsed;
    }

    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})[, ]+(\d{1,2}):(\d{2}):(\d{2})(?:\s*([AP]\.?M\.?))?$/i.exec(trimmed);
    if (match) {
        const [, month, day, year, hours, minutes, seconds, meridiem] = match;
        let hour = Number(hours);

        if (meridiem) {
            const isPm = meridiem.toUpperCase().startsWith('P');
            if (isPm && hour < 12) {
                hour += 12;
            } else if (!isPm && hour === 12) {
                hour = 0;
            }
        }

        return Date.UTC(Number(year), Number(month) - 1, Number(day), hour, Number(minutes), Number(seconds));
    }

    return undefined;
}

function isMissingWorkItem(details: AdoStatusDetails | undefined, nowMs: number): boolean {
    if (details?.status !== AdoStatus.ProductQueued || details?.workItemId != null) {
        return false;
    }

    // Without a usable timestamp the age cannot be established, so assume the release is still in
    // flight rather than offering a recovery action against a healthy package.
    const changedDateMs = parseAdoChangedDateUtc(details?.changedDate);

    return changedDateMs != null && nowMs - changedDateMs >= MISSING_WORK_ITEM_MIN_AGE_MS;
}

export function isAdoWorkItemMissing(adoStatus?: AdoStatusSummary, nowMs: number = Date.now()): boolean {
    if (adoStatus == null) {
        return false;
    }

    return isMissingWorkItem(adoStatus.marketplaceAdoStatus, nowMs) ||
        Object.values(adoStatus.contentAdoStatus ?? {}).some(details => isMissingWorkItem(details, nowMs));
}

export function isAPackItem(packageDetails?: MarketplaceSummaryData): boolean {
    let latest = getLatestMarketplacePackageSummaryResponse(packageDetails);
    return latest?.isAPackItem === true;
}

export interface PackItemReleaseStatus {
    packageName: string;
    marketplaceStatus?: SubmissionType;
    pcStatus?: SubmissionType;
    xboxStatus?: SubmissionType;
    aneidaStatus?: SubmissionType;
}

interface PackItemBrowserEntry {
    packageNameNoPrefix: string;
    fs24MarketplacePackageSummary?: MarketplaceSummaryData;
    fs24ContentPackageDetails?: ContentPackageDetails;
}

export function getPackItemsReleaseStatus(
    marketplacePackageSummary: MarketplaceSummaryData | undefined,
    marketplaceProductBrowserData: PackItemBrowserEntry[],
): PackItemReleaseStatus[] {
    if (!marketplacePackageSummary) {
        return [];
    }

    const latestMarketplacePackageDetails = getLatestMarketplacePackageSummaryResponse(marketplacePackageSummary);
    if (latestMarketplacePackageDetails?.packageContentType !== MisPackageContentType.Pack) {
        return [];
    }

    const referencedPackages = new Set(latestMarketplacePackageDetails.referencedPackages);
    const packItemEntries = marketplaceProductBrowserData.filter(item =>
        referencedPackages.has(getPackageName2024Prefix(item.packageNameNoPrefix)),
    );

    return packItemEntries.map(entry => {
        const ms = getLatestMarketplacePackageSummaryResponse(entry.fs24MarketplacePackageSummary);
        const cs = getLatestContentPackageDetailsResponse(entry.fs24ContentPackageDetails);
        return {
            packageName: ms?.packageName ?? (getPackageName2024Prefix(entry.packageNameNoPrefix) ?? entry.packageNameNoPrefix),
            marketplaceStatus: ms?.releaseStatus,
            pcStatus: cs?.PC?.releaseStatus,
            xboxStatus: cs?.Xbox?.releaseStatus,
            aneidaStatus: cs?.Aneida?.releaseStatus,
        };
    });
}

export function isPackItemReadyForStage(status: SubmissionType | undefined, transferToReleaseStage: boolean): boolean {
    if (transferToReleaseStage) {
        return status === SubmissionType.Release;
    }
    return status === SubmissionType.Prerelease || status === SubmissionType.Release;
}

export function parseSubmissionId(submissionId?: string) {
    const submissionIdParts = submissionId?.split(";");
    const timestamp = (submissionIdParts && submissionIdParts?.length > 2) ? submissionIdParts[1] : (submissionIdParts && submissionIdParts?.length > 1) ? submissionIdParts[0] : submissionId;

    return timestamp ? new Date(timestamp.replaceAll("_", ":")).toLocaleString() : submissionId;
}

export function parseMinimumGameVersion(manifestJson: Record<string, any>): string | undefined {
    let gameVersionDigits: string[] | undefined = undefined;

    if ("minimum_compatibility_version" in manifestJson) {
        gameVersionDigits = manifestJson["minimum_compatibility_version"].toString().split(".");
    } else if ("minimum_game_version" in manifestJson) {
        gameVersionDigits = manifestJson["minimum_game_version"].toString().split(".");
    }

    if (gameVersionDigits && gameVersionDigits.length === 3) {
        gameVersionDigits.push("0");
    }

    return gameVersionDigits ? gameVersionDigits.join(".") : undefined;
}
